import { FC } from "react";
import { CircularProgress, LinearProgress, Skeleton, Stack, TableCell, TableRow } from "@mui/material";
import { View } from "../layout/View";

interface Props {
	type: loaderType;
	boxHeight?: string | number;
	skeletonConfig?: skeletonConfig;
	indeterminateStyle?: indeterminateType;
}

/**
 * LoadingPlaceholder renders the content of LoadingView.
 *
 * @param type               the type of loading view (default: indeterminate)
 * @param skeletonConfig     if type is "skeleton", defines how many items are rendered (1, 2 or 3)
 *                           and their arrangement on screen
 * @param indeterminateStyle if type is "indeterminate", defines the style of the indeterminate
 *                           progress indicator
 * @returns                  the loading view
 */
const LoadingPlaceholder: FC<Props> = ({
	type = "indeterminate",
	indeterminateStyle = "circular",
	skeletonConfig = { count: 1, arrangement: "row" },
}) => {
	return (
		<>
			{type === "indeterminate" && <Indeterminate style={indeterminateStyle} />}
			{type === "skeleton" && <SkeletonItems config={skeletonConfig} />}
		</>
	);
};

export { LoadingPlaceholder };

/**
 * LoadingView leverages LoadingPlaceholder to render a view with indefinite loading
 * indicators such as skeletons or circular, indefinite progress indicators.
 *
 * LoadingView automatically disables application company selector and keeps the
 * current navigation stack.
 *
 * @param type               the type of loading view (default: indeterminate)
 * @param skeletonConfig     if type is "skeleton", defines how many items are rendered (1, 2 or 3)
 *                           and their arrangement on screen
 * @param indeterminateStyle if type is "indeterminate", defines the style of the indeterminate
 *                           progress indicator
 * @returns                  the loading view
 */
const LoadingView: FC<Props> = ({
	type = "indeterminate",
	indeterminateStyle = "circular",
	skeletonConfig = { count: 1, arrangement: "row" },
}) => {
	return (
		<View disableCompaniesSelector>
			<LoadingPlaceholder type={type} indeterminateStyle={indeterminateStyle} skeletonConfig={skeletonConfig} />
		</View>
	);
};

// Following types and components are used internally to manage render of:
// - indeterminate progress indicators (Indeterminate component);
// - skeletons (SkeletonItems).
type loaderType = "indeterminate" | "skeleton";
type indeterminateType = "circular" | "linear";

type skeletonArrangement = "row" | "column";
type skeletonConfig = {
	count: number;
	arrangement: skeletonArrangement;
};

const minSkeletonItems = 1;
const maxSkeletonItems = 3;

const Indeterminate: FC<{ style: indeterminateType }> = ({ style }) => {
	return (
		<Stack direction="column" alignItems="center">
			{style === "circular" && <CircularProgress />}
			{style === "linear" && <LinearProgress sx={{ width: "100%" }} />}
		</Stack>
	);
};

const SkeletonItems: FC<{ config: skeletonConfig }> = ({ config }) => {
	let count = config.count;
	if (count < minSkeletonItems) {
		count = minSkeletonItems;
	} else if (count > maxSkeletonItems) {
		count = maxSkeletonItems;
	}

	const items = Array.from(Array(count).keys());
	const direction = config.arrangement === "column" ? "row" : "column";

	return (
		<Stack spacing={4} direction={direction}>
			{items.map((i) => (
				<Stack key={i} direction="column" spacing={1} flexGrow={1}>
					<Skeleton variant="text" sx={{ flexGrow: 1, fontSize: "1rem" }} animation="wave" />
					<Skeleton variant="rounded" sx={{ flexGrow: 1 }} height={70} animation="wave" />
				</Stack>
			))}
		</Stack>
	);
};

/**
 * Renders the given number of table rows (1, 2 or 3) with the specified number of columns.
 *
 * @param rows
 * @param columns
 * @constructor
 */
export const SkeletonTableRow: FC<{ rows: 1 | 2 | 3; columns: number }> = ({ rows, columns }) => {
	const cells = (
		<>
			{Array.from({ length: columns }).map((_, k) => (
				<TableCell key={k}>
					<Skeleton sx={{ height: 24 }} />
				</TableCell>
			))}
		</>
	);

	return (
		<>
			{Array.from({ length: rows }).map((_, k) => (
				<TableRow key={k} className="loading">
					{cells}
				</TableRow>
			))}
		</>
	);
};

export default LoadingView;
